import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { defer, filter, forkJoin, map, type Observable, of, take } from 'rxjs';

import { type CanvasElementId } from '@blaze-runtime/engine-domain';
import { type PregenerationType } from '@blaze-shared/elements-domain';

import { BulkActionsFacade } from '../../+state/bulk-actions/bulk-actions.facade';
import {
  createRowActionPageFailure,
  createRowActionPageSuccess,
  RowActionPageGenerationFacade,
} from '../../+state/row-action-page-generation';
import { workflowActions } from '../../+state/runtime';
import { type BulkAction, type RowAction, type RowActionType } from '../models/actions';

import { type RecordClickIntent, toEngineRecordClickAction } from './record-actions.appearance';

const RECORD_ACTION_PAGE_PATHS = new Map<RowActionType, string>([
  ['view', 'content.itemView'],
  ['edit', 'content.itemEdit'],
  ['delete', 'content.itemDelete'],
]);

@Injectable({ providedIn: 'root' })
export class RecordActionsHandlerService {
  private readonly actions$ = inject(Actions);
  private readonly bulkActionsFacade = inject(BulkActionsFacade);
  private readonly rowActionPageGenerationFacade = inject(RowActionPageGenerationFacade);
  private readonly store = inject(Store);

  finalizeGeneratedPages(
    elementId: CanvasElementId,
    rowActions: RowAction[] | undefined,
    recordClick: RecordClickIntent | undefined,
  ): Observable<void> {
    const rowActionSources = (rowActions ?? [])
      .map((action) => {
        const path = RECORD_ACTION_PAGE_PATHS.get(action.$type);
        return path ? this.createRecordActionPageSource(elementId, action.$type, path) : undefined;
      })
      .filter((pageSource): pageSource is Observable<unknown> => !!pageSource);

    const recordClickSource = this.createRecordClickPageSource(elementId, recordClick);
    const sources = recordClickSource ? [...rowActionSources, recordClickSource] : rowActionSources;

    if (sources.length === 0) {
      return of(undefined);
    }

    return forkJoin(sources).pipe(map(() => undefined));
  }

  finalizeDeleteWorkflow(
    elementId: CanvasElementId,
    rowActions: RowAction[] | undefined,
  ): Observable<void> {
    if ((rowActions ?? []).every((action) => action.$type !== 'delete')) {
      return of(undefined);
    }

    return defer(() => {
      this.store.dispatch(
        workflowActions.generateDeleteRowWorkflow({ elementId, actionPath: 'content.itemDelete' }),
      );

      return this.actions$.pipe(
        ofType(
          workflowActions.createElementWorkflowSuccess,
          workflowActions.createElementWorkflowFailed,
        ),
        filter(
          (action) =>
            action.elementId === elementId && !!action.workflowIdPath?.includes('itemDelete'),
        ),
        take(1),
        map(() => undefined),
      );
    });
  }

  finalizeBulkActionWorkflows(
    elementId: CanvasElementId,
    bulkActions: BulkAction[] | undefined,
  ): Observable<void> {
    if (!Array.isArray(bulkActions)) {
      return of(undefined);
    }

    const sources = bulkActions
      .filter((bulkAction) => bulkAction.$type !== 'custom')
      .map((bulkAction) => {
        const bulkActionType = bulkAction.$type;

        return defer(() => {
          this.bulkActionsFacade.createBulkAction(elementId, bulkActionType);

          return this.actions$.pipe(
            ofType(
              workflowActions.createElementWorkflowSuccess,
              workflowActions.createElementWorkflowFailed,
            ),
            filter(
              (action) =>
                action.elementId === elementId &&
                !!action.workflowIdPath?.toLowerCase().includes(`bulk${bulkActionType}`),
            ),
            take(1),
          );
        });
      });

    if (sources.length === 0) {
      return of(undefined);
    }

    return forkJoin(sources).pipe(map(() => undefined));
  }

  finalizeAddNew(elementId: CanvasElementId, isAddNewEnabled: boolean): Observable<void> {
    if (!isAddNewEnabled) {
      return of(undefined);
    }

    return this.createRecordActionPageSource(elementId, 'add', 'content.itemAdd').pipe(
      map(() => undefined),
    );
  }

  private createRecordClickPageSource(
    elementId: CanvasElementId,
    recordClick: RecordClickIntent | undefined,
  ): Observable<unknown> | undefined {
    if (!recordClick || (recordClick.$type !== 'view' && recordClick.$type !== 'edit')) {
      return undefined;
    }

    const actionId = toEngineRecordClickAction(recordClick);

    return this.createRecordActionPageSource(
      elementId,
      recordClick.$type,
      `content.rowClick.targetId.${actionId}`,
    );
  }

  private createRecordActionPageSource(
    elementId: CanvasElementId,
    pregenerationType: PregenerationType,
    actionPath: string,
  ): Observable<unknown> {
    return defer(() => {
      this.rowActionPageGenerationFacade.tryCreateRowActionPage(elementId, {
        actionPath,
        pregenerationType,
      });

      return this.actions$.pipe(
        ofType(createRowActionPageSuccess, createRowActionPageFailure),
        filter(
          (action) =>
            action.elementId === elementId &&
            action.pregenerationType === pregenerationType &&
            action.actionPath === actionPath,
        ),
        take(1),
      );
    });
  }
}
